import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import React from "react";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useSettings } from "../../providers/settings-provider";

interface CalendarInputProps {
    label?: string | React.ReactNode;
    value: string;
    onChange: (date: string) => void;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    sx?: { [key: string]: any };
    placeholder?: string;
    disableStartAdornment?: boolean;
    minDate?: string;
}

const CalendarInput: React.FC<CalendarInputProps> = ({ label, value, onChange, error, helperText, disabled, sx, placeholder, disableStartAdornment, minDate }) => {
    const { formatDate, companySettings } = useSettings();
    const [calendarOpen, setCalendarOpen] = React.useState(false);
    const [pickerValue, setPickerValue] = React.useState<Dayjs | null>(value ? dayjs(value) : null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        // Update picker value when external value changes
        setPickerValue(value ? dayjs(value) : null);
    }, [value]);

    return (
        <Box flex={1}>
            { label && typeof label === 'string' 
                ? <Typography sx={{ fontSize: 13, mb: 0.5 }}>{label}</Typography> 
                : label
            }
            <TextField
                inputRef={inputRef}
                sx={{ width: sx?.width ?? '100%', ...sx}}
                disabled={disabled}
                size="small"
                placeholder={placeholder || "DD-MM-YYYY"}
                value={value ? formatDate(new Date(value)) : ''}
                onClick={() => setCalendarOpen(true)}
                InputProps={{
                    startAdornment: !disableStartAdornment && (
                        <InputAdornment position="start">
                            <CalendarTodayIcon
                                sx={{ color: 'var(--color-primary)', fontSize: 20, cursor: 'pointer' }}
                                onClick={() => setCalendarOpen(true)}
                            />
                        </InputAdornment>
                    ),
                    readOnly: true,
                }}
                error={error}
                helperText={helperText}
            />
            <DatePicker
                open={calendarOpen}
                disabled={disabled}
                onClose={() => setCalendarOpen(false)}
                value={pickerValue}
                onChange={(newValue: any) => {
                    const date = dayjs.isDayjs(newValue) ? newValue : (newValue ? dayjs(newValue) : null);
                    if (date && date.isValid()) {
                        // Return ISO format (YYYY-MM-DD)
                        const isoDate = date.format('YYYY-MM-DD');
                        onChange(isoDate);
                    }
                    setCalendarOpen(false);
                }}
                format={companySettings?.settings?.dateformat || 'DD-MM-YYYY'}
                minDate={minDate ? dayjs(minDate) : undefined}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                    textField: () => null,
                }}
                slotProps={{
                    popper: {
                        anchorEl: inputRef.current,
                    },
                }}
            />
        </Box>
    );
};

export default CalendarInput;